import { PageSEO, pageSEO } from '../components/PageSEO';
import React, { useState, useEffect } from 'react';
import { 
  getSubscriptionPlans, 
  getSubscriptionStatus, 
  createCheckoutSession,
  cancelSubscription,
  createPortalSession
} from '../lib/api';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Check,
  X,
  Loader2,
  Crown,
  Rocket,
  Building2,
  Sparkles,
  ExternalLink
} from 'lucide-react';

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const [plansRes, statusRes] = await Promise.all([
        getSubscriptionPlans(),
        getSubscriptionStatus()
      ]);
      setPlans(plansRes.data.plans || {});
      setCurrentStatus(statusRes.data);
    } catch (error) {
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }

  function checkPaymentStatus() {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      toast.success('Payment successful! Your subscription is now active.');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchData();
    }
  }

  async function handleSubscribe(planKey) {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    setCheckingOut(planKey);
    try {
      const originUrl = window.location.origin;
      const res = await createCheckoutSession(planKey, originUrl);
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setCheckingOut(null);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      await cancelSubscription();
      toast.success('Your subscription will be cancelled at the end of the billing period');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  }

  async function handleManageSubscription() {
    try {
      const returnUrl = window.location.origin + '/pricing';
      const res = await createPortalSession(returnUrl);
      
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (error) {
      toast.error('Failed to open billing portal');
    }
  }

  function getPlanIcon(planKey) {
    switch (planKey) {
      case 'free':
        return <Sparkles className="w-8 h-8" />;
      case 'pro':
        return <Rocket className="w-8 h-8" />;
      case 'business':
        return <Building2 className="w-8 h-8" />;
      default:
        return <Crown className="w-8 h-8" />;
    }
  }

  function getPlanColor(planKey) {
    switch (planKey) {
      case 'free':
        return 'text-muted-foreground';
      case 'pro':
        return 'text-[hsl(var(--accent))]';
      case 'business':
        return 'text-purple-500';
      default:
        return 'text-muted-foreground';
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  const planOrder = ['free', 'pro', 'business'];
  const sortedPlans = planOrder
    .filter(key => plans[key])
    .map(key => ({ key, ...plans[key] }));

  return (
    <div className="p-4 sm:p-6 space-y-8">
      <PageSEO {...pageSEO.pricing} />
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Choose Your Plan</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Unlock powerful features to streamline your tax tracking and maximize your deductions
        </p>
      </div>

      {currentStatus && currentStatus.plan !== 'free' && (
        <Card className="max-w-2xl mx-auto border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[hsl(var(--accent))]" />
                  <span className="font-semibold">
                    Currently on {plans[currentStatus.plan]?.name || 'Premium'}
                  </span>
                  <Badge variant="outline" className="ml-2">
                    {currentStatus.status}
                  </Badge>
                </div>
                {currentStatus.cancel_at_period_end && (
                  <p className="text-sm text-orange-500">
                    Cancels at end of billing period
                  </p>
                )}
              </div>
              <Button variant="outline" onClick={handleManageSubscription}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Manage Billing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {sortedPlans.map((plan) => {
          const isCurrentPlan = currentStatus?.plan === plan.key;
          const isPro = plan.key === 'pro';

          return (
            <Card 
              key={plan.key}
              className={`relative overflow-hidden transition-all ${
                isPro 
                  ? 'border-[hsl(var(--accent))] shadow-lg shadow-[hsl(var(--accent))]/10 scale-105' 
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              {isPro && (
                <div className="absolute top-0 right-0 bg-[hsl(var(--accent))] text-white px-3 py-1 text-xs font-semibold rounded-bl-lg">
                  POPULAR
                </div>
              )}

              <CardHeader className="text-center pb-2">
                <div className={`mx-auto p-3 rounded-full bg-muted/50 ${getPlanColor(plan.key)}`}>
                  {getPlanIcon(plan.key)}
                </div>
                <CardTitle className="text-xl mt-4">{plan.name}</CardTitle>
                <CardDescription>
                  <span className="text-3xl font-bold text-foreground">
                    ${plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-muted-foreground">/{plan.interval || 'month'}</span>
                  )}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-4">
                  {isCurrentPlan ? (
                    <Button 
                      className="w-full" 
                      variant="outline" 
                      disabled
                      data-testid={`current-plan-${plan.key}`}
                    >
                      Current Plan
                    </Button>
                  ) : plan.key === 'free' ? (
                    currentStatus?.plan !== 'free' ? (
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={handleCancel}
                        disabled={cancelling}
                        data-testid="downgrade-to-free-button"
                      >
                        {cancelling ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Downgrade to Free
                      </Button>
                    ) : (
                      <Button 
                        className="w-full" 
                        variant="outline" 
                        disabled
                        data-testid="current-plan-free"
                      >
                        Current Plan
                      </Button>
                    )
                  ) : (
                    <Button 
                      className={`w-full ${isPro ? 'btn-primary' : ''}`}
                      variant={isPro ? 'default' : 'outline'}
                      onClick={() => handleSubscribe(plan.key)}
                      disabled={checkingOut === plan.key}
                      data-testid={`subscribe-${plan.key}-button`}
                    >
                      {checkingOut === plan.key ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Redirecting...
                        </>
                      ) : (
                        <>Subscribe to {plan.name}</>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center text-sm text-muted-foreground max-w-xl mx-auto space-y-2">
        <p>
          All plans come with a 30-day money-back guarantee. Cancel anytime.
        </p>
        <p>
          Secure payments powered by Stripe. Your payment information is never stored on our servers.
        </p>
      </div>
    </div>
  );
}
